import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { recruitingPill, positionChip, classChip, truncationNotice, reportState } from '../lib/reportWeb';
import './ReportLandingPage.css';

// Copy-link label reverts after this long. HANDOFF.md §6.
const COPIED_LABEL_MS = 1800;

// Verified seal — HANDOFF.md §7. Used identically in table cells, card stats
// and the footnote. `label` set => aria-label="Verified" (a meaningful icon
// in that context); omitted => aria-hidden (the surrounding text already
// says "Verified" so the footnote's icon is decorative).
function VerifiedSeal({ className, label }) {
  const a11yProps = label ? { 'aria-label': label } : { 'aria-hidden': true };
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" className={className} {...a11yProps}>
      <path
        fill="#2563EB"
        d="M12 1l2.9 2.1 3.5-.4 1.4 3.3 3.1 1.8-1 3.4 1 3.4-3.1 1.8-1.4 3.3-3.5-.4L12 23l-2.9-2.1-3.5.4-1.4-3.3L1.1 16.2l1-3.4-1-3.4 3.1-1.8L5.6 2.7l3.5.4z"
      />
      <path fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" d="M8 12l3 3 5-6" />
    </svg>
  );
}

export default function ReportLandingPage() {
  const { code } = useParams();
  const [web, setWeb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => {
    fetch(`${window.location.origin}/api/reports/public/${encodeURIComponent(code)}`)
      .then((res) => { if (!res.ok) throw new Error('not found'); return res.json(); })
      .then((data) => {
        setWeb(data.web);
        document.title = `${data.web.report.title} — ProZpects Coach Report`;
      })
      .catch(() => setError('Report not found or no longer available.'))
      .finally(() => setLoading(false));
  }, [code]);

  // Reports are shared, not public. The crawler path (Caddy → /api/reports/og/)
  // also sets this; a human landing here directly must get it too.
  useEffect(() => {
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex';
    document.head.appendChild(tag);
    return () => { document.head.removeChild(tag); };
  }, []);

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  // Never surfaces an error: a clipboard permission failure still swaps the
  // label, because the user can select the address bar either way.
  const copyLink = () => {
    const done = () => {
      setCopied(true);
      copyTimer.current = setTimeout(() => setCopied(false), COPIED_LABEL_MS);
    };
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(window.location.href).then(done, done);
      else done();
    } catch (e) { done(); }
  };

  if (loading) return <div className="rw"><div className="rw__state">Loading…</div></div>;
  if (error) return <div className="rw"><div className="rw__state"><h2>Report Not Found</h2><p>{error}</p></div></div>;

  const { report, columns, rows } = web;
  const notice = truncationNotice(report);
  const state = reportState(web);

  return (
    <div className="rw">
      {/* Sticky header band + 4px accent rule — HANDOFF.md §5 */}
      <div className="rw__headerWrap" data-print="header">
        <div className="rw__band">
          <img src="/logo-wordmark-white.png" alt="ProZpects" className="rw__wordmark" />
          <div className="rw__titleblock">
            <div className="rw__title">{report.title}</div>
            <div className="rw__meta">
              <span className="rw__metaGroup">
                <span className="rw__metaLabel">Sport</span>{' '}
                <span className="rw__metaValue">{report.sport_display}</span>
              </span>
              <span className="rw__metaSep" />
              <span className="rw__metaGroup">
                <span className="rw__metaLabel">Athletes</span>{' '}
                <span className="rw__metaValue">{report.athlete_count}</span>
              </span>
              <span className="rw__metaSep" />
              <span className="rw__metaGroup">
                <span className="rw__metaLabel">Generated</span>{' '}
                <span className="rw__metaValue">{report.generated_date}</span>
              </span>
            </div>
          </div>
          <div className="rw__qrGroup">
            <div className="rw__qrCaption">Scan for<br />live report</div>
            <div className="rw__qrTile">
              {/* Decorative: the adjacent caption already says what it is, and
                  the code it encodes is the page the reader is already on. */}
              <img src={web.report_qr} alt="" />
            </div>
          </div>
        </div>
        <div className="rw__accent" />
      </div>

      {/* Toolbar — HANDOFF.md §6 */}
      <div className="rw__toolbar" data-print="hide">
        <div className="rw__toolbarLeft">
          <a href="/login" className="rw__toolbarCta">
            <span>Log in for full profiles</span>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M5 12h13M12 5l7 7-7 7" />
            </svg>
          </a>
          <div className="rw__reportCode">
            <span className="rw__reportCodeLabel">Report</span>
            <span className="rw__reportCodeValue">{report.code}</span>
          </div>
        </div>
        <div className="rw__toolbarRight">
          <button type="button" className="rw__toolbtn" onClick={copyLink}>
            {copied ? 'Link copied' : 'Copy link'}
          </button>
          <button type="button" className="rw__toolbtn" onClick={() => window.print()}>
            Print / Save PDF
          </button>
        </div>
      </div>

      {state === 'unavailable' ? (
        <section className="rw__section rw__section--unavailable">
          <div className="rw__unavailableBox">
            <h2 className="rw__unavailableHeading">This list is empty right now</h2>
            <p className="rw__unavailableBody">
              There's nobody on this list right now, but the link is still live—it will start working again the moment the coach adds someone. You can still log in to ProZpects to find athletes yourself.
            </p>
          </div>
        </section>
      ) : (
        <>
          {/* Spreadsheet — HANDOFF.md §7 */}
          <section className="rw__section rw__section--table">
            <div className="rw__tableWrap" data-print="scroll">
              <table className="rw__table">
                <thead>
                  <tr>
                    <th />
                    <th>Name</th>
                    {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.user_id} className={i % 2 ? 'rw__tr--alt' : undefined}>
                      <td className="rw__tdAvatar">
                        {row.photo_url ? (
                          <img src={row.photo_url} alt="" className="rw__avatarImg" />
                        ) : (
                          <div className="rw__avatarFallback">{row.initials}</div>
                        )}
                      </td>
                      <td className="rw__tdName">
                        <a href={row.profile_link} target="_blank" rel="noopener" className="rw__nameLink">
                          {row.name}
                        </a>
                      </td>
                      {row.cells.map((cell, ci) => (
                        <td key={columns[ci].key} className="rw__td">
                          <div className="rw__cellInner">
                            <span>{cell.value}</span>
                            {cell.verified && (
                              <span className="rw__seal rw__seal--cell">
                                <VerifiedSeal label="Verified" />
                              </span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rw__footnote">
              <span className="rw__seal">
                <VerifiedSeal />
              </span>
              <span>{report.footer_note}</span>
              {notice && <span className="rw__truncationNotice">· {notice}</span>}
            </div>
          </section>

          {/* Player Highlights — HANDOFF.md §8, §8.1 */}
          <section className="rw__section rw__section--highlights" data-print="break">
            <div className="rw__highlightsHead">
              <div className="rw__highlightsTitle">Player Highlights</div>
              <div className="rw__highlightsSub">Tap a card or scan its code to open the athlete's live ProZpects profile</div>
            </div>
            <div className="rw__grid">
              {rows.map((row) => {
                const pill = recruitingPill(row.card);
                const position = positionChip(row.card);
                const classLabel = classChip(row.card);
                return (
                  <a
                    key={row.user_id}
                    href={row.profile_link}
                    target="_blank"
                    rel="noopener"
                    className="rw__card"
                    data-print="card"
                  >
                    <div className="rw__cardHeader">
                      {row.photo_url ? (
                        <img src={row.photo_url} alt="" className="rw__cardPhoto" />
                      ) : (
                        <div className="rw__cardPhotoFallback">{row.initials}</div>
                      )}
                      <div className="rw__cardText">
                        <div className="rw__cardName">{row.name}</div>
                        <div className="rw__cardChips">
                          {position && <span className="rw__chipPosition">{position}</span>}
                          {classLabel && <span className="rw__chipClass">{classLabel}</span>}
                        </div>
                        <div className="rw__cardSchool">{row.card.high_school}</div>
                      </div>
                    </div>
                    <div className="rw__cardStats">
                      {row.card.stats.map((stat, si) => (
                        <div key={si} className="rw__statCell">
                          <div className="rw__statLabel">{stat.label}</div>
                          <div className="rw__statValue">
                            <span>{stat.value}</span>
                            {stat.verified && (
                              <span className="rw__seal">
                                <VerifiedSeal label="Verified" />
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="rw__cardFooter">
                      <div>
                        {pill && (
                          <span className={`rw__pill rw__pill--${pill.tone}`}>{pill.text}</span>
                        )}
                      </div>
                      {/* Decorative: the enclosing anchor already announces the
                          athlete's name and destination. */}
                      <img src={row.card.qr} alt="" className="rw__cardQr" />
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        </>
      )}

      {/* Login CTA band — HANDOFF.md §8.2 */}
      <section className="rw__cta" data-print="hide">
        <div className="rw__ctaCopy">
          <div className="rw__ctaHeading">See the full board, not just this page</div>
          <div className="rw__ctaBody">
            Log in to open verified film, full measurable history, transcripts and contact info
            for every athlete in this report — and build your own boards.
          </div>
        </div>
        <div className="rw__ctaButtons">
          <a href="/login" className="rw__ctaPrimary">Log in to ProZpects</a>
          <a href="/signup" className="rw__ctaSecondary">Create a coach account</a>
        </div>
      </section>

      {/* Page footer — HANDOFF.md §8.3 */}
      <footer className="rw__footer">
        <span>ProZpects™ · Verified athletic data, coach-ready profiles · app.prozpects.com</span>
        <span className="rw__footerCode">Report {report.code}</span>
      </footer>
    </div>
  );
}
